import type { PropsWithChildren } from "react";

import type { TextProps as BaseTextProps } from "tamagui";
import { Text as BaseText } from "tamagui";

import { ColorToken } from "@/ui/theme/tokens";
import { Font } from "@/ui/theme/typography";

type TypographyVariant =
  | "body"
  | "bodyMedium"
  | "caption"
  | "captionMedium"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "overline"
  | "overlineMedium";

interface TextAttributes {
  fontFamily: Font;
  fontSize: number;
  letterSpacing: number;
  lineHeight: number;
  maxFontSizeMultiplier: number;
  textAlign?: BaseTextProps["textAlign"];
}

/**
 * Enum representing the maximum font size multipliers for different text categories.
 *
 * - `Body`: Multiplier for body text to control maximum scaling (e.g., for accessibility).
 * - `Captions`: Multiplier for caption text, ensuring it remains readable without excessive scaling.
 * - `Headers`: Multiplier for header text to limit scaling and maintain layout consistency.
 * - `SmallContent`: Multiplier for small content text, ensuring readability without excessive scaling.
 */
enum MaxFontSizeMultiplier {
  Body = 1.5,
  Captions = 1.4,
  Headers = 1.2,
  SmallContent = 1.1,
}

const TextAttributesMapper: Record<TypographyVariant, TextAttributes> = {
  body: {
    fontFamily: Font.Body,
    fontSize: 15,
    letterSpacing: -0.03,
    lineHeight: 1.25,
    maxFontSizeMultiplier: MaxFontSizeMultiplier.Body,
  },
  bodyMedium: {
    fontFamily: Font.BodyMedium,
    fontSize: 15,
    letterSpacing: -0.03,
    lineHeight: 1.25,
    maxFontSizeMultiplier: MaxFontSizeMultiplier.Body,
  },
  caption: {
    fontFamily: Font.Body,
    fontSize: 13,
    letterSpacing: -0.03,
    lineHeight: 1.25,
    maxFontSizeMultiplier: MaxFontSizeMultiplier.Captions,
  },
  captionMedium: {
    fontFamily: Font.BodyMedium,
    fontSize: 13,
    letterSpacing: -0.03,
    lineHeight: 1.25,
    maxFontSizeMultiplier: MaxFontSizeMultiplier.Captions,
  },
  h1: {
    fontFamily: Font.Heading,
    fontSize: 32,
    letterSpacing: 0,
    lineHeight: 1.3,
    maxFontSizeMultiplier: MaxFontSizeMultiplier.Headers,
  },
  h2: {
    fontFamily: Font.Heading,
    fontSize: 28,
    letterSpacing: 0,
    lineHeight: 1.3,
    maxFontSizeMultiplier: MaxFontSizeMultiplier.Headers,
  },
  h3: {
    fontFamily: Font.Heading,
    fontSize: 24,
    letterSpacing: 0,
    lineHeight: 1.3,
    maxFontSizeMultiplier: MaxFontSizeMultiplier.Headers,
  },
  h4: {
    fontFamily: Font.Heading,
    fontSize: 21,
    letterSpacing: 0,
    lineHeight: 1.3,
    maxFontSizeMultiplier: MaxFontSizeMultiplier.Headers,
  },
  h5: {
    fontFamily: Font.Heading,
    fontSize: 19,
    letterSpacing: 0,
    lineHeight: 1.3,
    maxFontSizeMultiplier: MaxFontSizeMultiplier.Headers,
  },
  overline: {
    fontFamily: Font.Body,
    fontSize: 11,
    letterSpacing: -0.03,
    lineHeight: 1.25,
    maxFontSizeMultiplier: MaxFontSizeMultiplier.SmallContent,
  },
  overlineMedium: {
    fontFamily: Font.BodyMedium,
    fontSize: 11,
    letterSpacing: -0.03,
    lineHeight: 1.25,
    maxFontSizeMultiplier: MaxFontSizeMultiplier.SmallContent,
  },
};

interface TextProps extends BaseViewProps, PropsWithChildren {
  backgroundColor?: BaseTextProps["backgroundColor"];
  color?: BaseTextProps["color"];
  flexShrink?: BaseTextProps["flexShrink"];
  isDisabled?: boolean;
  numberOfLines?: BaseTextProps["numberOfLines"];
  opacity?: BaseTextProps["opacity"];
  role?: BaseTextProps["role"];
  shouldShowTrailingEllipses?: boolean;
  textAlign?: BaseTextProps["textAlign"];
  textDecorationLine?: BaseTextProps["textDecorationLine"];
  variant?: TypographyVariant;
}

const Text = ({
  accessibilityLabel,
  backgroundColor,
  children,
  color = ColorToken.NeutralPrimary,
  flexShrink,
  isDisabled,
  numberOfLines,
  opacity,
  role,
  shouldShowTrailingEllipses,
  testID,
  textAlign,
  textDecorationLine,
  variant = "body",
}: TextProps) => {
  const textAttributes = TextAttributesMapper[variant];
  const {
    fontFamily,
    fontSize,
    letterSpacing,
    lineHeight,
    maxFontSizeMultiplier,
  } = textAttributes;

  return (
    <BaseText
      aria-label={accessibilityLabel}
      backgroundColor={backgroundColor}
      color={color}
      disabled={isDisabled}
      ellipsizeMode={shouldShowTrailingEllipses ? "tail" : undefined}
      flexShrink={flexShrink}
      fontFamily={fontFamily}
      fontSize={fontSize}
      letterSpacing={fontSize * letterSpacing}
      lineHeight={lineHeight * fontSize}
      maxFontSizeMultiplier={maxFontSizeMultiplier}
      numberOfLines={numberOfLines}
      opacity={opacity}
      role={role}
      testID={testID}
      textAlign={textAlign}
      textDecorationLine={textDecorationLine}
    >
      {children}
    </BaseText>
  );
};

export type { TextProps, TypographyVariant };
export { TextAttributesMapper };
export default Text;
